import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, CheckCircle, ArrowRight, Download, School, Calculator } from 'lucide-react';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Generador de DDJJ para Escuelas Privadas
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Simplifica la generación de Declaraciones Juradas para tu institución educativa.
            Proceso seguro, rápido y confiable.
          </p>
          <Link
            to="/form"
            className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors duration-200"
          >
            Comenzar ahora
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
            Características principales
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Formato Compatible</h3>
              <p className="text-gray-600">
                Genera archivos en el formato requerido por el sistema de la DGCyE.
              </p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Validación Automática</h3>
              <p className="text-gray-600">
                Verifica automáticamente la información ingresada para evitar errores.
              </p>
            </div>
            <div className="p-6 bg-gray-50 rounded-lg">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                <Download className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Descarga Inmediata</h3>
              <p className="text-gray-600">
                Obtén tu archivo ZIP listo para presentar en segundos.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* SIPEE Promotion Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                ¿Necesitas un sistema completo de liquidación de sueldos?
              </h2>
              <p className="text-xl mb-8 text-blue-100">
                Conoce SIPEE, el sistema integral de gestión administrativa para escuelas privadas.
              </p>
              <ul className="space-y-4 mb-8">
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-blue-200" />
                  <span>Liquidación de sueldos automatizada</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-blue-200" />
                  <span>Gestión de personal docente y no docente</span>
                </li>
                <li className="flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-blue-200" />
                  <span>Generación automática de DDJJ</span>
                </li>
              </ul>
              <a
                href="https://sipee.com.ar"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-6 py-3 border-2 border-white text-base font-medium rounded-md text-white hover:bg-white hover:text-blue-600 transition-colors duration-200"
              >
                Conocer más sobre SIPEE
                <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
            <div className="hidden md:block">
              <div className="bg-white p-8 rounded-lg shadow-xl">
                <div className="flex items-center justify-center mb-6">
                  <School className="h-16 w-16 text-blue-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 text-center mb-4">
                  SIPEE
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  Sistema Integral para la Gestión de Escuelas Privadas
                </p>
                <div className="flex justify-center">
                  <Calculator className="h-24 w-24 text-blue-600 opacity-50" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Help Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">
            ¿Necesitas ayuda?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Consulta nuestras guías de uso o ponte en contacto con nosotros
          </p>
          <div className="flex justify-center space-x-4">
            <Link
              to="/cargos"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              Ver nomenclador
            </Link>
            <Link
              to="/escalas"
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-base font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200"
            >
              Consultar escalas
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}